import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { getEcho, interpError, interpErrorBody, listEcho, saveEcho } from "./interpReviewApi";
import { Button, Card, DraftBanner, ProgressBar, SectionTitle, Segmented, StatusPill, WorksheetTable } from "./ui";

/*
 * Echo draft review: per study, the worksheet measurements next to the AI draft interpretation. The reviewer can
 * edit the text and choose Approve / Approve with edits / Reject (with a reason). The server keeps the AI original
 * and every edited version (for diffing and learning). Draft -- not filed to chart: nothing is signed or sent.
 */

const DECISIONS = [["approve", "Approve"], ["approve_with_edits", "Approve with edits"], ["reject", "Reject"]];

const norm = (t) => String(t || "").replace(/\r\n/g, "\n").trim().split("\n").map((l) => l.replace(/\s+$/, "")).join("\n");
export const isEdited = (text, original) => norm(text) !== norm(original);

// Keep Approve / Approve-with-edits consistent with whether the text was changed (the server enforces the same).
export const fitDecision = (decision, edited) => {
  if (decision === "approve" && edited) return "approve_with_edits";
  if (decision === "approve_with_edits" && !edited) return "approve";
  return decision;
};

const EchoTab = ({ initialItem, onItemChange, toast, onProgress }) => {
  const [list, setList] = useState(null);
  const [listError, setListError] = useState(null);
  const [selectedId, setSelectedId] = useState(initialItem || null);
  const [detail, setDetail] = useState(null);
  const [text, setText] = useState("");
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);
  // After a 409 (saved elsewhere): the text this tab tried to save, offered back -- never silently dropped or forced.
  const [lostText, setLostText] = useState(null);

  const loadList = useCallback(async () => {
    try {
      const r = await listEcho();
      setList(r);
      setListError(null);
      if (onProgress) onProgress(r.progress);
      return r;
    } catch (e) {
      setListError(interpError(e, "Could not load the echo drafts"));
      return null;
    }
  }, [onProgress]);

  useEffect(() => {
    loadList().then((r) => {
      if (!r) return;
      setSelectedId((cur) => (cur && r.items.some((i) => i.item_id === cur) ? cur
        : (r.items.find((i) => i.status !== "submitted") || r.items[0] || {}).item_id || null));
    });
  }, [loadList]);

  useEffect(() => {
    if (!selectedId) return undefined;
    let live = true;
    setDetail(null);
    setProblem(null);
    setLostText(null);
    getEcho(selectedId)
      .then((r) => {
        if (!live) return;
        const resp = r.response;
        setDetail(r);
        setText(resp && resp.edited_text != null ? resp.edited_text : r.item.draft_text);
        setDecision(resp?.decision || null);
        setReason(resp?.reject_reason || "");
        setDirty(false);
      })
      .catch((e) => live && setProblem(interpError(e, "Could not load this study")));
    return () => {
      live = false;
    };
  }, [selectedId]);

  // Report the open item to the page (URL ?item=) through a ref, so a new callback never refetches the item.
  const onItemChangeRef = useRef(onItemChange);
  onItemChangeRef.current = onItemChange;
  useEffect(() => {
    if (selectedId && onItemChangeRef.current) onItemChangeRef.current(selectedId);
  }, [selectedId]);

  const original = detail ? (detail.response?.original_text ?? detail.item.draft_text) : "";
  const edited = isEdited(text, original);

  const choose = (id) => {
    if (id === selectedId) return;
    if (dirty && !window.confirm("You have unsaved changes on this study. Leave without saving?")) return;
    setSelectedId(id);
  };

  // 409: someone (another tab / device) saved this study first. Load the latest saved version and its revision so
  // the next save is a deliberate one; keep this tab's text so it can be restored.
  const reloadAfterConflict = async () => {
    const mine = text;
    try {
      const r = await getEcho(detail.item.item_id);
      const resp = r.response;
      setDetail(r);
      setText(resp && resp.edited_text != null ? resp.edited_text : r.item.draft_text);
      setDecision(resp?.decision || null);
      setReason(resp?.reject_reason || "");
      setDirty(false);
      setLostText(mine);
      setProblem("This study was changed on another tab or device. The latest saved version is shown; your text was "
        + "not saved. Use \u201cRestore my text\u201d to put it back, then save again.");
      loadList();
    } catch (e2) {
      setProblem(interpError(e2, "This study was changed elsewhere and could not be reloaded"));
    }
  };

  const save = async (submit) => {
    if (!detail || saving) return;
    setSaving(true);
    setProblem(null);
    try {
      const r = await saveEcho(detail.item.item_id, {
        edited_text: text,
        decision: fitDecision(decision, edited),
        reject_reason: reason,
        submit,
        revision: detail.response ? detail.response.revision : 0,
      });
      setDetail((d) => ({ ...d, response: r.response }));
      setDecision(r.response.decision || null);
      setDirty(false);
      await loadList();
      toast(submit ? "Decision saved (not filed to chart)" : "Draft saved", "success");
    } catch (e) {
      if (e?.response?.status === 409 && interpErrorBody(e)?.code === "CONFLICT") {
        await reloadAfterConflict();
      } else {
        setProblem(interpError(e, "Could not save"));
      }
    } finally {
      setSaving(false);
    }
  };


  if (listError) return <Card className="p-6 text-[13px] text-red-700">{listError}</Card>;
  if (!list) return <div className="flex justify-center py-16"><CircularProgress size={28} /></div>;
  if (!list.items.length) return <Card className="p-6 text-[13px] text-[#6B7280]">No echo drafts are loaded yet.</Card>;

  const item = detail?.item;
  const progress = list.progress || { total: 0, submitted: 0 };

  return (
    <div className="flex flex-col gap-4">
      <DraftBanner />
      <Card className="px-4 py-3 flex flex-wrap items-center gap-3">
        <span className="text-[13px] font-semibold text-[#1D2951]">Reviewed</span>
        <ProgressBar done={progress.submitted} total={progress.total} label="Echo review progress" />
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-[260px_1fr] gap-4">
        <Card className="max-h-[70vh] overflow-auto">
          <SectionTitle>Studies</SectionTitle>
          <ul data-testid="echo-list">
            {list.items.map((i) => (
              <li key={i.item_id}>
                <button type="button" onClick={() => choose(i.item_id)}
                  aria-current={i.item_id === selectedId ? "true" : undefined}
                  className={`w-full min-h-[48px] text-left px-4 py-2 border-t border-[#F1F2F4] flex items-center justify-between gap-2 ${
                    i.item_id === selectedId ? "bg-[#E8F0FB]" : "hover:bg-[#F7F9FC]"}`}>
                  <span>
                    <span className="block text-[14px] font-medium text-[#18181B]">{i.label}</span>
                    <span className="block text-[11px] text-[#6B7280]">{i.study_ref}</span>
                  </span>
                  <StatusPill status={i.status} decision={i.decision} />
                </button>
              </li>
            ))}
          </ul>
        </Card>

        {!detail ? (
          problem ? <Card className="p-6 text-[13px] text-red-700">{problem}</Card>
            : <div className="flex justify-center py-16"><CircularProgress size={28} /></div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-[#18181B]">{item.label}</h2>
              <span className="text-[13px] text-[#6B7280]">{item.study_type}</span>
              <StatusPill status={detail.response?.status || "new"} decision={detail.response?.decision} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <SectionTitle>Worksheet measurements</SectionTitle>
                <div className="pb-3"><WorksheetTable rows={item.worksheet} /></div>
                {item.missing_data?.length > 0 && (
                  <div className="px-4 pb-4">
                    <div className="text-[12px] font-semibold text-[#6B7280] mb-1">Not on the worksheet</div>
                    <ul className="list-disc pl-5 text-[13px] text-[#374151]" data-testid="missing-data">
                      {item.missing_data.map((m, i) => <li key={i}>{m}</li>)}
                    </ul>
                  </div>
                )}
              </Card>
              <Card>
                <SectionTitle right={
                  <button type="button" className="text-[13px] text-[#1D2951] underline h-10 px-2 disabled:opacity-50"
                    disabled={!edited} onClick={() => { setText(original); setDecision((d) => fitDecision(d, false)); setDirty(true); }}>
                    Reset to AI draft
                  </button>}>
                  AI draft interpretation {edited && <span className="ml-1 normal-case text-sky-700">(edited)</span>}
                </SectionTitle>
                <div className="px-4 pb-4">
                  <textarea aria-label="Interpretation text" data-testid="echo-text"
                    className="w-full min-h-[50vh] rounded-lg border border-[#D7DCE3] p-3 font-mono text-[13px] leading-[1.5]"
                    value={text} onChange={(e) => { setText(e.target.value); setDirty(true); }} />
                  {item.model && <div className="mt-1 text-[11px] text-[#9CA3AF]">AI draft ({item.model})</div>}
                </div>
              </Card>
            </div>

            <Card>
              <SectionTitle>Decision</SectionTitle>
              <div className="px-4 pb-4 flex flex-col gap-3">
                <Segmented options={DECISIONS} value={fitDecision(decision, edited)} label="Decision" testid="decision"
                  onChange={(v) => { setDecision(v); setDirty(true); }} />
                {decision === "reject" && (
                  <textarea aria-label="Reason for rejecting" className="w-full min-h-[72px] rounded-lg border border-[#D7DCE3] p-2 text-[14px]"
                    placeholder="Why is this draft rejected?" value={reason}
                    onChange={(e) => { setReason(e.target.value); setDirty(true); }} />
                )}
                {problem && <div className="text-[13px] text-red-700" role="alert">{problem}</div>}
                {lostText != null && (
                  <div>
                    <Button variant="secondary" onClick={() => { setText(lostText); setLostText(null); setDirty(true); }}>
                      Restore my text
                    </Button>
                  </div>
                )}
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="secondary" disabled={saving} onClick={() => save(false)}>Save draft</Button>
                  <Button disabled={saving || !decision} onClick={() => save(true)}>Save decision</Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default EchoTab;
